import { Injectable, inject } from '@angular/core';

import { mergeMap, tap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { ApplicationsConfigurationService, VppApplicationsConfigurationService } from '@era/settings-sdk';
import { forkJoin } from 'rxjs';
import { safeMap } from '../effects.helpers';
import { ScheduledApplicationsRequestService } from './scheduled-applications-request-service';
import { ApplicationsApiActions, ScheduledApplicationsResolverActions } from './scheduled-applications.actions';

@Injectable()
export class ScheduledApplicationsEffects {
  private readonly actions = inject(Actions);
  private readonly applicationService = inject(ApplicationsConfigurationService);
  private readonly vppApplicationsConfigurationService = inject(VppApplicationsConfigurationService);
  private readonly scheduledApplicationsRequestService = inject(ScheduledApplicationsRequestService);

  /**
   * The (re)load of the scheduled applications has been requested.
   * Effect: we load the state from the api.
   */
  scheduledApplicationsRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ScheduledApplicationsResolverActions.scheduledApplicationsRequested),
      mergeMap(() =>
        safeMap(
          forkJoin({
            regular: this.applicationService.getAllScheduledApplications(),
            vpp: this.vppApplicationsConfigurationService.getAllScheduledVppApplications(),
          }),
          ({ regular, vpp }) =>
            ApplicationsApiActions.scheduledApplicationsLoaded({
              applications: [
                ...regular,
                ...vpp.map((v) => ({
                  locationDisplayName: v.locationDisplayName,
                  environmentName: v.environmentName,
                  bundleIdentifier: v.bundleIdentifier,
                  version: v.version,
                  applyFrom: v.applyFrom,
                })),
              ],
            }),
          (error) => ApplicationsApiActions.scheduledApplicationsLoadedError({ error })
        )
      )
    );
  });

  /**
   * The CSV file download of the scheduled applications has been requested.
   * Effect: we load the state from the api.
   */
  scheduledApplicationsCsvRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ScheduledApplicationsResolverActions.scheduledApplicationsCSVDownloadRequested),
      mergeMap(() =>
        safeMap(
          this.scheduledApplicationsRequestService.getAllScheduledApplicationsCsv().pipe(
            tap((res: Blob) => {
              const today = new Date();
              const month: string = (today.getMonth() + 1).toString();
              const day: string = today.getDate().toString();
              const year: string = today.getFullYear().toString();
              const formattedDate = `${month}-${day}-${year}`;
              this.createDownloadFile(res, 'text/csv', `scheduled-applications-overview-${formattedDate}`);
            })
          ),
          () => ApplicationsApiActions.scheduledApplicationsCSVDownloaded(),
          (error) => ApplicationsApiActions.scheduledApplicationsCSVDownloadedError({ error })
        )
      )
    );
  });

  private createDownloadFile(res: Blob, type: string, name: string) {
    // Create a new blob to add the file extension
    const blob = new Blob([res], { type: type });
    // Trigger the download
    const uri = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.download = name;
    anchor.href = uri;
    anchor.click();
  }
}
